import React from 'react';

import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { StructuredListItem } from '@saas-ui/react';

import {
  getNotificationColorConfig,
  NotificationIcon,
  NotificationMailIcon,
} from '@/components/common';
import { IconButton } from '@/components/core';
import { FONTS_MORE_OPACITY } from '@/constant';
import { Notification } from '@/types/notifications';
import { formatRelativeTime } from '@/utils';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  onMarkAsRead: (notification: Notification) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onMarkAsRead,
}) => {
  const colorConfig = getNotificationColorConfig(notification.type);
  const hasTargetUrl = Boolean(notification.targetURL);

  // Keep onClick off StructuredListItem — SaaS UI wraps clickable items in an
  // inner button that drops item-level gap/align styles and lets the icon cell
  // shrink under the message text.
  return (
    <StructuredListItem
      borderBottom='1px solid'
      borderColor='border.light'
      bg='cards.bg'
      transition='all 0.2s'
      p={0}
    >
      <HStack
        w='full'
        px={4}
        py={3}
        gap={3}
        align='flex-start'
        cursor={hasTargetUrl ? 'pointer' : 'default'}
        onClick={() => onClick(notification)}
        _hover={hasTargetUrl ? { bg: 'whiteAlpha.50' } : undefined}
      >
        <Box flexShrink={0} pt={0.5}>
          <NotificationIcon
            type={notification.type}
            agentId={notification.agents_id}
          />
        </Box>
        <VStack
          flex={1}
          minW={0}
          align='stretch'
          gap={1}
          position='relative'
        >
          <IconButton
            aria-label='Mark as read'
            variant='glass-item'
            onClick={e => {
              e.stopPropagation();
              onMarkAsRead(notification);
            }}
            icon={<NotificationMailIcon read={false} />}
            position='absolute'
            right={0}
            top={0}
          />
          <Text
            mt={1.5}
            mr={12}
            background='white'
            backgroundClip='text'
            size='secondarySemiBold'
            color={colorConfig.bg}
          >
            {notification.title}
          </Text>
          <Text size='secondaryRegular' whiteSpace='pre-line' opacity={0.8}>
            {notification.message}
          </Text>
          <Text size='additionalRegular' opacity={FONTS_MORE_OPACITY}>
            {formatRelativeTime(new Date(notification.created_at))}
          </Text>
        </VStack>
      </HStack>
    </StructuredListItem>
  );
};
